export const ITEM_LIST_MAX_ROWS = 20;
export const ITEM_LIST_MAX_DESCRIPTION = 100;
export const ITEM_LIST_MAX_LENGTH = 4000;

export interface ItemListRow {
  description: string;
  quantity: number;
  unitPriceCents: number;
}

export interface ItemListValue { version: 1; items: ItemListRow[]; }

export function readItemList(raw: unknown): ItemListValue | null {
  if (raw == null || raw === '') return { version: 1, items: [] };
  if (typeof raw !== 'string' || raw.length > ITEM_LIST_MAX_LENGTH) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || value.version !== 1 || !Array.isArray(value.items) || value.items.length > ITEM_LIST_MAX_ROWS) return null;
    if (!value.items.every(isItemListRow)) return null;
    return { version: 1, items: value.items };
  } catch { return null; }
}

export function isItemListRow(row: unknown): row is ItemListRow {
  if (!row || typeof row !== 'object') return false;
  const r = row as ItemListRow;
  return typeof r.description === 'string' && r.description.trim().length > 0
    && r.description.length <= ITEM_LIST_MAX_DESCRIPTION
    && typeof r.quantity === 'number' && Number.isFinite(r.quantity)
    && r.quantity > 0 && r.quantity <= 9999.999
    && r.quantity === Math.round(r.quantity * 1000) / 1000
    && Number.isInteger(r.unitPriceCents) && r.unitPriceCents >= 0 && r.unitPriceCents <= 99999999;
}

export function isItemListValueValid(raw: unknown, required = false): boolean {
  const value = readItemList(raw);
  return value !== null && (!required || value.items.length > 0);
}

// Round each line in cents before summing, matching the API's decimal arithmetic.
export function itemListRowTotal(row: ItemListRow): number {
  return Math.floor((Math.round(row.quantity * 1000) * row.unitPriceCents + 500) / 1000);
}

export function itemListTotal(items: ItemListRow[]): number {
  return items.reduce((total, row) => total + itemListRowTotal(row), 0);
}

export function itemListFormulaValue(raw: unknown): number | null {
  const value = readItemList(raw);
  return value ? itemListTotal(value.items) / 100 : null;
}

export function formatItemListMoney(cents: number, locale = 'pt-BR'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

export function formatItemListSummary(raw: unknown, locale = 'pt-BR'): string {
  const value = readItemList(raw);
  if (!value?.items.length) return '—';
  const count = value.items.length;
  const noun = locale === 'pt-BR' ? (count === 1 ? 'item' : 'itens') : (count === 1 ? 'item' : 'items');
  return `${count} ${noun} · ${formatItemListMoney(itemListTotal(value.items), locale)}`;
}

/** Accept decimal comma/dot and Brazilian grouping, without silently truncating precision. */
export function parseItemListNumber(raw: string, places: 2 | 3): number | null {
  let text = raw.trim().replace(/^R\$\s*/, '');
  if (text.includes(',')) {
    if (!new RegExp(`^(?:\\d+|\\d{1,3}(?:\\.\\d{3})+)(?:,\\d{1,${places}})?$`).test(text)) return null;
    text = text.replace(/\./g, '').replace(',', '.');
  } else if (!new RegExp(`^\\d+(?:\\.\\d{1,${places}})?$`).test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  const scaled = Number(whole) * 10 ** places + Number(fraction.padEnd(places, '0'));
  return Number.isSafeInteger(scaled) ? scaled : null;
}
